import {
  normalizePayloadValue,
  UPLOAD_PROGRESS_MAX,
} from 'helpers/publishProgressUtils';
import {
  submitPublishRequest,
  uploadPublishImage,
  uploadPublishImageAsset,
  uploadPublishVideo,
} from 'helpers/publishJobUtils';
import {
  runPatchPublishJob,
  runSingleImagePatchPublishJob,
  runImageDtoPatchPublishJob,
} from 'helpers/stmarysPublishJobs';

export const runBannerPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const editing = job?.mode === 'EDIT' && job?.recordId;
  const needsWebUpload = Boolean(files?.webFile);
  const needsMobileUpload = Boolean(files?.mobileFile);

  if (!editing && (!needsWebUpload || !needsMobileUpload)) {
    throw new Error('Both web image and mobile image are required.');
  }

  const webImage = await uploadPublishImage({
    file: files?.webFile,
    existingUrl: files?.existingWebImage || job.payload?.webImage,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-web-image',
    rangeStart: 0,
    rangeEnd: needsMobileUpload ? 40 : UPLOAD_PROGRESS_MAX,
  });

  const mobileImage = await uploadPublishImage({
    file: files?.mobileFile,
    existingUrl: files?.existingMobileImage || job.payload?.mobileImage,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-mobile-image',
    rangeStart: needsWebUpload ? 40 : 0,
    rangeEnd: UPLOAD_PROGRESS_MAX,
  });

  if (!webImage || !mobileImage) {
    throw new Error('Both web image and mobile image are required.');
  }

  return submitPublishRequest({
    job,
    createPath: '/banner/create',
    updatePath: (id) => `/banner/update/${id}`,
    payload: {
      ...job.payload,
      webImage,
      mobileImage,
      title: normalizePayloadValue(job.payload?.title ?? job.title),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel: 'banner',
  });
};

export const runTemplatePublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };
  let uploadPercent = 0;

  const fcmImage = await uploadPublishImage({
    file: files?.fcmFile,
    existingUrl: files?.existingFcmImage || payload?.fcm?.image,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-fcm-image',
    rangeStart: 0,
    rangeEnd: files?.whatsappFile ? 42 : UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (fcmImage) {
    payload.fcm = {
      ...payload.fcm,
      image: fcmImage,
    };
    uploadPercent = files?.whatsappFile ? 42 : UPLOAD_PROGRESS_MAX;
  }

  const whatsappImage = await uploadPublishImage({
    file: files?.whatsappFile,
    existingUrl: files?.existingWhatsappImage || payload?.whatsapp?.image,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-whatsapp-image',
    rangeStart: uploadPercent,
    rangeEnd: UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (whatsappImage) {
    payload.whatsapp = {
      ...payload.whatsapp,
      image: whatsappImage,
    };
    uploadPercent = UPLOAD_PROGRESS_MAX;
  }

  return submitPublishRequest({
    job,
    createPath: '/template/create/admin',
    updatePath: (id) => `/template/update/admin/${id}`,
    payload: {
      ...payload,
      name: normalizePayloadValue(payload.name ?? job.title),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel: 'template',
    uploadPercent: uploadPercent || UPLOAD_PROGRESS_MAX,
  });
};

export const runPagesPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };
  const imageItems = Array.isArray(files?.imageItems) ? files.imageItems : [];
  const fileItems = Array.isArray(files?.fileItems) ? files.fileItems : [];
  const totalUploads = imageItems.length + fileItems.length;

  if (!imageItems.length) {
    throw new Error('Add atleast one image');
  }

  if (!imageItems.some((item) => item?.mainImage === 'Yes')) {
    throw new Error('Select a main image from images!');
  }

  const uploadedImages = [];
  let uploadCursor = 0;

  for (let i = 0; i < imageItems.length; i += 1) {
    const item = imageItems[i];
    const rangeStart =
      totalUploads > 0
        ? Math.round((uploadCursor / totalUploads) * UPLOAD_PROGRESS_MAX)
        : 0;
    uploadCursor += 1;
    const rangeEnd =
      totalUploads > 0
        ? Math.round((uploadCursor / totalUploads) * UPLOAD_PROGRESS_MAX)
        : UPLOAD_PROGRESS_MAX;

    const uploaded = await uploadPublishImageAsset({
      file: item?.file,
      existingUrl: item?.existingUrl,
      existingThumbnail: item?.existingThumbnail,
      accessToken,
      signal,
      onProgress,
      phase: 'uploading-image',
      rangeStart,
      rangeEnd,
      thumbnailRequired: 'Yes',
    });

    if (!uploaded?.url) {
      throw new Error('Image upload failed');
    }

    if (!uploaded?.thumbnail) {
      throw new Error('Image thumbnail is required');
    }

    uploadedImages.push({
      url: uploaded.url,
      thumbnail: uploaded.thumbnail,
      mainImage: item?.mainImage,
      imageTag: item?.imageTag,
    });
  }

  const uploadedFiles = [];
  for (let i = 0; i < fileItems.length; i += 1) {
    const item = fileItems[i];
    const rangeStart =
      totalUploads > 0
        ? Math.round((uploadCursor / totalUploads) * UPLOAD_PROGRESS_MAX)
        : 0;
    uploadCursor += 1;
    const rangeEnd =
      totalUploads > 0
        ? Math.round((uploadCursor / totalUploads) * UPLOAD_PROGRESS_MAX)
        : UPLOAD_PROGRESS_MAX;

    const uploaded = await uploadPublishImageAsset({
      file: item?.file,
      existingUrl: item?.existingUrl,
      accessToken,
      signal,
      onProgress,
      phase: 'uploading-file',
      rangeStart,
      rangeEnd,
      thumbnailRequired: 'No',
    });

    if (uploaded?.url) {
      uploadedFiles.push(uploaded.url);
    }
  }

  return submitPublishRequest({
    job,
    createPath: '/Pages/create',
    updatePath: (id) => `/Pages/update/${id}`,
    payload: {
      ...payload,
      images: uploadedImages,
      files: uploadedFiles,
      name: normalizePayloadValue(payload.name ?? job.title),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel: 'page',
    uploadPercent: UPLOAD_PROGRESS_MAX,
  });
};

const runSimpleEntityPublishJob = async ({
  job,
  accessToken,
  onProgress,
  signal,
  createPath,
  updatePath,
  entityLabel,
  titleField = 'name',
  updateMethod = 'PUT',
}) => {
  const payload = { ...job.payload };

  return submitPublishRequest({
    job,
    createPath,
    updatePath,
    payload: {
      ...payload,
      ...(titleField && payload[titleField] !== undefined
        ? { [titleField]: normalizePayloadValue(payload[titleField] ?? job.title) }
        : {}),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel,
    uploadPercent: 0,
    updateMethod,
  });
};

export const runCategoriesPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/pagesCategory/create',
    updatePath: (id) => `/pagesCategory/update/${id}`,
    entityLabel: 'category',
    titleField: 'name',
  });

export const runWidgetsPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/widgets/create/admin',
    updatePath: (id) => `/widgets/update/admin/${id}`,
    entityLabel: 'widget',
    titleField: 'title',
  });

export const runSeoPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/pages-seo/create',
    updatePath: (id) => `/pages-seo/update/${id}`,
    entityLabel: 'SEO entry',
    titleField: 'metatitle',
  });

export const runInformationPolicyPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/information-policy/add',
    updatePath: (id) => `/information-policy/update/${id}`,
    entityLabel: 'information policy',
    titleField: null,
    updateMethod: 'PATCH',
  });

export const runConfigSettingsPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/config-settings/update/admin',
    updatePath: (id) => `/config-settings/update/admin/${id}`,
    entityLabel: 'config settings',
    titleField: null,
    updateMethod: 'PUT',
  });

export const runAdminSettingsPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };
  const hasFavicon = Boolean(files?.faviconFile || files?.existingFavicon);

  const logo = await uploadPublishImage({
    file: files?.logoFile,
    existingUrl: files?.existingLogo || payload?.logo,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-logo',
    rangeStart: 0,
    rangeEnd: hasFavicon ? 50 : UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (logo) {
    payload.logo = logo;
  }

  const favIconImage = await uploadPublishImage({
    file: files?.faviconFile,
    existingUrl: files?.existingFavicon || payload?.favIconImage,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-favicon',
    rangeStart: hasFavicon ? 50 : UPLOAD_PROGRESS_MAX,
    rangeEnd: UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (favIconImage) {
    payload.favIconImage = favIconImage;
  }

  return submitPublishRequest({
    job,
    createPath: '/AdminSettings/update/admin',
    updatePath: (id) => `/AdminSettings/update/admin/${id}`,
    payload,
    accessToken,
    signal,
    onProgress,
    entityLabel: 'settings',
    uploadPercent: UPLOAD_PROGRESS_MAX,
    updateMethod: 'PATCH',
  });
};

export const runGalleryPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };
  const galleryImages = (files?.galleryImages || []).filter(
    (image) => image?.file || image?.existingUrl,
  );
  const galleryVideos = files?.galleryVideos || [];
  const galleryAudios = files?.galleryAudios || [];
  const isUploadMediaType = (mediaType) =>
    mediaType === 'Uploaded' || mediaType === 'Upload';
  const resolveItemVideoType = (video = {}, payloadVideo = {}) =>
    video.videoType || payloadVideo.videoType || 'Youtube';
  const resolveItemAudioType = (audio = {}, payloadAudio = {}) =>
    audio.audioType || payloadAudio.audioType || 'Youtube';
  const payloadVideos = payload.videos || [];
  const payloadAudios = payload.audio || [];
  const hasUploadVideos = galleryVideos.some((video, index) =>
    isUploadMediaType(resolveItemVideoType(video, payloadVideos[index] || {})),
  );
  const hasYoutubeVideos = payloadVideos.some(
    (video, index) =>
      !isUploadMediaType(
        resolveItemVideoType(galleryVideos[index] || {}, video),
      ),
  );
  const hasUploadAudios = galleryAudios.some(
    (audio, index) =>
      isUploadMediaType(
        resolveItemAudioType(audio, payloadAudios[index] || {}),
      ) && (audio?.file || audio?.existingUrl),
  );
  const hasYoutubeAudios = payloadAudios.some(
    (audio, index) =>
      !isUploadMediaType(
        resolveItemAudioType(galleryAudios[index] || {}, audio),
      ),
  );
  const hasImages = galleryImages.length > 0;
  const hasVideos =
    hasUploadVideos || hasYoutubeVideos || galleryVideos.length > 0;
  const hasAudios =
    hasUploadAudios || hasYoutubeAudios || galleryAudios.length > 0;
  const isMetadataOnlyEdit =
    job.mode === 'EDIT' && !hasImages && !hasVideos && !hasAudios;

  if (!isMetadataOnlyEdit && !hasImages && !hasVideos && !hasAudios) {
    throw new Error('Add at least one image, video or audio.');
  }

  if (hasImages && !galleryImages.some((image) => image.mainImage === 'Yes')) {
    throw new Error('Select a main image from images.');
  }

  if (isMetadataOnlyEdit) {
    const submitPayload = { ...payload };
    delete submitPayload.thumbnailRequired;
    delete submitPayload.videoThumbnailRequired;
    delete submitPayload.images;
    delete submitPayload.videos;
    delete submitPayload.audio;
    delete submitPayload.videoType;
    delete submitPayload.type;

    return submitPublishRequest({
      job,
      createPath: '/image/create',
      updatePath: (recordId) => `/image/admin/update/${recordId}`,
      payload: {
        ...submitPayload,
        title: normalizePayloadValue(submitPayload.title ?? job.title),
        priorityOrder: Number(submitPayload.priorityOrder),
      },
      accessToken,
      signal,
      onProgress,
      entityLabel: 'gallery',
      uploadPercent: UPLOAD_PROGRESS_MAX,
      updateMethod: 'PATCH',
    });
  }

  const needsVideoUpload = hasUploadVideos;
  const imageUploadEnd =
    hasImages && (needsVideoUpload || galleryVideos.length > 0)
      ? 50
      : hasImages
        ? UPLOAD_PROGRESS_MAX
        : 0;
  const previewUploadEnd =
    galleryVideos.length > 0
      ? needsVideoUpload
        ? 70
        : UPLOAD_PROGRESS_MAX
      : imageUploadEnd || UPLOAD_PROGRESS_MAX;
  const uploadedImages = [];
  const thumbnailRequired =
    String(payload.thumbnailRequired || 'yes').toLowerCase() === 'yes'
      ? 'yes'
      : 'no';
  const videoThumbnailRequired =
    String(payload.videoThumbnailRequired || 'yes').toLowerCase() === 'yes'
      ? 'yes'
      : 'no';

  const splitProgressRange = (start, end, index, total) => {
    if (!total) {
      return { start, end };
    }
    const slice = (end - start) / total;
    return {
      start: Math.round(start + slice * index),
      end: Math.round(start + slice * (index + 1)),
    };
  };

  const buildGalleryThumbnail = (url, existingThumbnail) => {
    if (existingThumbnail) {
      return existingThumbnail;
    }
    return url?.replace(/\.\w+$/, '-thumbnail.webp');
  };

  if (hasImages) {
    for (let i = 0; i < galleryImages.length; i++) {
      const image = galleryImages[i];
      const range = splitProgressRange(0, imageUploadEnd, i, galleryImages.length);

      const uploadResult = await uploadPublishImageAsset({
        file: image.file,
        existingUrl: image.existingUrl,
        existingThumbnail: image.existingThumbnail,
        accessToken,
        signal,
        onProgress,
        phase: 'uploading-gallery-image',
        rangeStart: range.start,
        rangeEnd: range.end,
        thumbnailRequired,
      });

      if (!uploadResult?.url) {
        throw new Error('Image upload failed.');
      }

      uploadedImages.push({
        url: uploadResult.url,
        thumbnail: uploadResult.thumbnail || buildGalleryThumbnail(uploadResult.url, image.existingThumbnail),
        mainImage: image.mainImage,
        imageTag: image.imageTag,
        name: image.name || image.imageTag || payload.title || job.title,
        status: image.status || 'Show',
      });
    }
  }

  if (hasImages) {
    payload.images = uploadedImages;
  } else {
    delete payload.images;
  }

  if (galleryVideos.length > 0 || hasYoutubeVideos) {
    const uploadedVideos = [];
    const videoCount = Math.max(galleryVideos.length, payloadVideos.length);

    for (let i = 0; i < videoCount; i++) {
      const video = galleryVideos[i] || {};
      const payloadVideo = payloadVideos[i] || {};
      const itemVideoType = resolveItemVideoType(video, payloadVideo);
      const previewRange = splitProgressRange(
        imageUploadEnd,
        previewUploadEnd,
        i,
        videoCount,
      );

      let previewImage = null;
      if (video.previewFile || video.existingPreviewUrl) {
        const previewUploadResult = await uploadPublishImageAsset({
          file: video.previewFile,
          existingUrl: video.existingPreviewUrl,
          accessToken,
          signal,
          onProgress,
          phase: 'uploading-gallery-image',
          rangeStart: previewRange.start,
          rangeEnd: previewRange.end,
          thumbnailRequired: 'yes',
        });

        previewImage = previewUploadResult?.url;

        if (!previewImage) {
          throw new Error('Preview image upload failed.');
        }
      }

      if (!isUploadMediaType(itemVideoType)) {
        const youtubeUrl = String(payloadVideo.url || '').trim();
        if (!youtubeUrl) {
          throw new Error('Video URL is required.');
        }
        uploadedVideos.push({
          url: youtubeUrl,
          videoType: itemVideoType,
          videoTag: video.videoTag || payloadVideo.videoTag,
          name: video.name || payloadVideo.name || payload.title || job.title,
          status: video.status || payloadVideo.status || 'Show',
          ...(previewImage
            ? { previewImage, thumbnail: previewImage }
            : {}),
        });
        continue;
      }

      const videoRange = splitProgressRange(
        previewUploadEnd,
        UPLOAD_PROGRESS_MAX,
        i,
        videoCount,
      );

      const uploadResult = await uploadPublishVideo({
        file: video.file,
        existingUrl: video.existingUrl,
        existingThumbnail: previewImage,
        accessToken,
        signal,
        onProgress,
        phase: 'uploading-gallery-video',
        rangeStart: videoRange.start,
        rangeEnd: videoRange.end,
        thumbnailRequired: videoThumbnailRequired,
      });

      const url =
        typeof uploadResult === 'string'
          ? uploadResult
          : uploadResult?.original;

      if (!url) {
        continue;
      }

      const uploadedThumbnail =
        typeof uploadResult === 'object' ? uploadResult?.thumbnail : null;

      uploadedVideos.push({
        url,
        videoType: itemVideoType,
        videoTag: video.videoTag || payloadVideo.videoTag,
        name: video.name || payloadVideo.name || payload.title || job.title,
        status: video.status || payloadVideo.status || 'Show',
        ...(previewImage ? { previewImage } : {}),
        thumbnail: previewImage || uploadedThumbnail || url,
      });
    }

    payload.videos = uploadedVideos;
  }

  if (galleryAudios.length > 0 || hasYoutubeAudios) {
    const uploadedAudios = [];
    const audioCount = Math.max(galleryAudios.length, payloadAudios.length);

    for (let i = 0; i < audioCount; i += 1) {
      const audio = galleryAudios[i] || {};
      const payloadAudio = payloadAudios[i] || {};
      const itemAudioType = resolveItemAudioType(audio, payloadAudio);
      const range = splitProgressRange(
        0,
        UPLOAD_PROGRESS_MAX,
        i,
        audioCount,
      );

      let previewImage = null;
      if (audio.previewFile || audio.existingPreviewUrl) {
        const previewUploadResult = await uploadPublishImageAsset({
          file: audio.previewFile,
          existingUrl: audio.existingPreviewUrl,
          accessToken,
          signal,
          onProgress,
          phase: 'uploading-gallery-image',
          rangeStart: range.start,
          rangeEnd: range.end,
          thumbnailRequired: 'yes',
        });
        previewImage = previewUploadResult?.url;
      }

      if (!isUploadMediaType(itemAudioType)) {
        const youtubeUrl = String(payloadAudio.url || '').trim();
        if (!youtubeUrl) {
          throw new Error('Audio URL is required.');
        }
        uploadedAudios.push({
          url: youtubeUrl,
          audioType: itemAudioType,
          audioTag: audio.audioTag || payloadAudio.audioTag,
          name:
            audio.name ||
            payloadAudio.name ||
            payload.title ||
            job.title,
          status: audio.status || payloadAudio.status || 'Show',
          ...(previewImage
            ? { previewImage, thumbnail: previewImage }
            : {}),
        });
        continue;
      }

      const audioUrl = await uploadPublishImage({
        file: audio.file,
        existingUrl: audio.existingUrl,
        accessToken,
        signal,
        onProgress,
        phase: 'uploading-gallery-audio',
        rangeStart: range.start,
        rangeEnd: range.end,
        thumbnailRequired: 'No',
      });

      if (!audioUrl) {
        throw new Error('Audio upload failed.');
      }

      uploadedAudios.push({
        url: audioUrl,
        audioType: itemAudioType,
        audioTag: audio.audioTag || payloadAudio.audioTag,
        name: audio.name || payloadAudio.name || payload.title || job.title,
        status: audio.status || payloadAudio.status || 'Show',
        ...(previewImage
          ? { previewImage, thumbnail: previewImage }
          : {}),
      });
    }

    payload.audio = uploadedAudios;
  }

  const submitPayload = { ...payload };
  delete submitPayload.thumbnailRequired;
  delete submitPayload.videoThumbnailRequired;
  delete submitPayload.videoType;

  // Prefer explicit form-selected type from payload; fall back only if missing.
  if (!submitPayload.type) {
    submitPayload.type = hasAudios
      ? 'Audio'
      : hasVideos && !hasImages
        ? 'Video'
        : 'Image';
  }

  if (submitPayload.type === 'Video' || submitPayload.type === 'Audio') {
    delete submitPayload.images;
  }
  if (submitPayload.type !== 'Video') {
    delete submitPayload.videos;
  }
  if (submitPayload.type !== 'Audio') {
    delete submitPayload.audio;
  }
  if (submitPayload.type !== 'Image') {
    delete submitPayload.thumbnailRequired;
  }
  if (submitPayload.type !== 'Video') {
    delete submitPayload.videoThumbnailRequired;
  }

  return submitPublishRequest({
    job,
    createPath: '/image/create',
    updatePath: (recordId) => `/image/admin/update/${recordId}`,
    payload: {
      ...submitPayload,
      title: normalizePayloadValue(submitPayload.title ?? job.title),
      priorityOrder: Number(submitPayload.priorityOrder),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel: 'gallery',
    uploadPercent: UPLOAD_PROGRESS_MAX,
    updateMethod: 'PATCH',
  });
};

export const runOfficialContactPublishJob = (params) =>
  runPatchPublishJob({
    ...params,
    createPath: '/official-contact/create',
    updatePath: (id) => `/official-contact/admin/update/${id}`,
    entityLabel: 'official contact',
    titleField: 'name',
  });

export const runAdministrationPublishJob = (params) =>
  runSingleImagePatchPublishJob({
    ...params,
    createPath: '/administration/create',
    updatePath: (id) => `/administration/admin/update/${id}`,
    entityLabel: 'administration',
    titleField: 'name',
    imageOptional: true,
  });

export const runParishPriestPublishJob = (params) =>
  runSingleImagePatchPublishJob({
    ...params,
    createPath: '/parish-priest/create',
    updatePath: (id) => `/parish-priest/update/${id}`,
    entityLabel: 'parish priest',
    titleField: 'name',
  });

export const runLeadershipHistoryPublishJob = (params) =>
  runSingleImagePatchPublishJob({
    ...params,
    createPath: '/leadership-history/create',
    updatePath: (id) => `/leadership-history/update/${id}`,
    entityLabel: 'leadership history',
    titleField: 'name',
  });

export const runHousePublishJob = (params) =>
  runSingleImagePatchPublishJob({
    ...params,
    createPath: '/house/admin/create',
    updatePath: (id) => `/house/admin/update/${id}`,
    entityLabel: 'house',
    titleField: 'houseName',
  });

export const runHistoryPublishJob = (params) =>
  runSingleImagePatchPublishJob({
    ...params,
    createPath: '/history/create',
    updatePath: (id) => `/history/update/${id}`,
    entityLabel: 'history',
    titleField: 'name',
  });

export const runHierarchyPublishJob = (params) =>
  runSingleImagePatchPublishJob({
    ...params,
    createPath: '/hierarchy/admin/create',
    updatePath: (id) => `/hierarchy/admin/update/${id}`,
    entityLabel: 'hierarchy',
    titleField: 'name',
  });

export const runChurchPublishJob = (params) =>
  runImageDtoPatchPublishJob({
    ...params,
    createPath: '/church/admin/create',
    updatePath: (id) => `/church/admin/update/${id}`,
    entityLabel: 'church',
    titleField: 'name',
  });

export const runOrganizationPublishJob = (params) =>
  runSingleImagePatchPublishJob({
    ...params,
    createPath: '/organization/create',
    updatePath: (id) => `/organization/update/${id}`,
    entityLabel: 'organization',
    titleField: 'name',
    // Swagger declares PUT /organization/update/{id}
    updateMethod: 'PUT',
  });

export const runMembersPublishJob = (params) =>
  runSingleImagePatchPublishJob({
    ...params,
    createPath: '/members/create',
    updatePath: (id) => `/members/update/${id}`,
    entityLabel: 'member',
    titleField: 'name',
    imageOptional: true,
  });

export const runChapelPublishJob = (params) =>
  runImageDtoPatchPublishJob({
    ...params,
    createPath: '/chapel/admin/create',
    updatePath: (id) => `/chapel/admin/update/${id}`,
    entityLabel: 'chapel',
    titleField: 'name',
  });

export const runNewsPublishJob = (params) =>
  runImageDtoPatchPublishJob({
    ...params,
    createPath: '/news/admin/create',
    updatePath: (id) => `/news/admin/update/${id}`,
    entityLabel: 'news',
    titleField: 'name',
  });

export const runFestivalsPublishJob = (params) =>
  runSingleImagePatchPublishJob({
    ...params,
    createPath: '/festivals/admin/create',
    updatePath: (id) => `/festivals/admin/update/${id}`,
    entityLabel: 'festival',
    titleField: 'title',
  });

export const runAdvertisementPublishJob = (params) =>
  runImageDtoPatchPublishJob({
    ...params,
    createPath: '/advertisement/admin/create',
    updatePath: (id) => `/advertisement/admin/update/${id}`,
    entityLabel: 'advertisement',
    titleField: 'name',
  });

export const runOccasionPublishJob = (params) =>
  runSingleImagePatchPublishJob({
    ...params,
    createPath: '/occasion/admin/create',
    updatePath: (id) => `/occasion/admin/update/${id}`,
    entityLabel: 'occasion',
    titleField: 'name',
  });

export const runLiveStreamingPublishJob = (params) =>
  runPatchPublishJob({
    ...params,
    createPath: '/live-streaming/admin/create',
    updatePath: (id) => `/live-streaming/admin/update/${id}`,
    entityLabel: 'live streaming',
    titleField: 'title',
  });

export const runIncomePdfLinkPublishJob = async (params) => {
  try {
    return await runSingleImagePatchPublishJob({
      ...params,
      createPath: '/income-pdf-link/admin/create',
      updatePath: (id) => `/income-pdf-link/admin/update/${id}`,
      entityLabel: 'income PDF link',
      titleField: 'name',
      imageField: 'pdf',
      // Same filemanager/images/upload API as images/videos; omit thumbnailRequired.
      thumbnailRequired: null,
    });
  } catch (error) {
    if (error?.message === 'Image is required.') {
      throw new Error('PDF is required.');
    }
    throw error;
  }
};

export const runEventsPublishJob = (params) =>
  runSingleImagePatchPublishJob({
    ...params,
    createPath: '/blogs/admin/create',
    updatePath: (id) => `/blogs/admin/update/${id}`,
    entityLabel: 'event',
    titleField: 'name',
  });

export const runQuotesPublishJob = (params) =>
  runSingleImagePatchPublishJob({
    ...params,
    createPath: '/quotes/admin/create',
    updatePath: (id) => `/quotes/admin/update/${id}`,
    entityLabel: 'quote',
    titleField: 'title',
  });

export const runPositionPublishJob = (params) =>
  runPatchPublishJob({
    ...params,
    createPath: '/position/create',
    updatePath: (id) => `/position/update/${id}`,
    entityLabel: 'position',
    titleField: 'name',
  });
